import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import Portal from '../../shared/Portal';
import {
  getMembers, removeMember,
  getMemberNotes, saveMemberNotes,
  getDepartments, createDepartment, deleteDepartment, setMemberDept,
  createBizInvite, getBizInvites, deleteBizInvite, updateMemberRole,
  searchUsers,
} from './businessApi';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const BASE_SEATS = 10;
const MAX_SEATS  = 250;
const DEPT_COLORS = ['#5B9ED0','#4AC87A','#C8A84B','#C05050','#7B6FD0','#D4873A','#2A9E82'];


function ScoreBar({ label, score, color }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#6b7a99', marginBottom: 2 }}>
        <span>{label}</span>
        <span style={{ color, fontWeight: 700 }}>{score}%</span>
      </div>
      <div style={{ height: 4, background: `${color}22`, borderRadius: 3 }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function BurnoutCircle({ risk }) {
  const size = 46;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - risk / 100);
  const color = risk >= 65 ? '#C05050' : risk >= 40 ? '#C8A84B' : '#4AC87A';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}22`} strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={`${circ}`} strokeDashoffset={`${offset}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 4} textAnchor="middle" fill={color} fontSize="10" fontWeight="700">{risk}%</text>
    </svg>
  );
}

const ROLES = [
  { id: 'admin',   label: 'Admin',   nl: 'Admin',   desc: 'Full access, cannot delete business', color: '#9B59B6' },
  { id: 'manager', label: 'Manager', nl: 'Manager', desc: 'Team, Reports, Programs, Agenda',      color: '#5B9ED0' },
  { id: 'hr',      label: 'HR',      nl: 'HR',       desc: 'Reports, ROI, RI&E, Benchmarks',      color: '#2A9E82' },
  { id: 'member',  label: 'Member',  nl: 'Lid',      desc: 'Dashboard, Pulse, Programs, Agenda',  color: C.silver },
];

function RoleBadge({ role, nl }) {
  const r = ROLES.find(x => x.id === role) || { label: role || 'Member', color: C.silver };
  if (role === 'owner') return (
    <span style={{ fontSize: 9, color: '#C8A84B', background: '#C8A84B18', border: '1px solid #C8A84B44', borderRadius: 6, padding: '2px 7px', fontWeight: 700 }}>
      OWNER
    </span>
  );
  return (
    <span style={{ fontSize: 9, color: r.color, background: `${r.color}18`, border: `1px solid ${r.color}44`, borderRadius: 6, padding: '2px 7px', fontWeight: 700 }}>
      {nl ? (r.nl || r.label) : r.label}
    </span>
  );
}

const APP_URL = window.location.origin;

export default function BusinessMembersScreen({ lang, businessId, bizName, currentUserId, isOwner, role: myRole, onMemberClick }) {
  const nl = lang === 'nl';
  const canManage = isOwner || myRole === 'admin' || myRole === 'manager';
  const [subTab, setSubTab] = useState('members');

  // Members
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);

  // Invite panel
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]  = useState('member');
  const [generating, setGenerating]  = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkCopied, setLinkCopied]  = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteError, setInviteError] = useState('');

  // User search (invite existing SL users directly)
  const [userQuery, setUserQuery]   = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // {id, name, picture}
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef(null);

  // Notes
  const [notesTarget, setNotesTarget] = useState(null);
  const [noteText, setNoteText]       = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaved, setNoteSaved]     = useState(false);

  // Departments
  const [depts, setDepts]           = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(true);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptColor, setNewDeptColor] = useState(DEPT_COLORS[0]);
  const [creatingDept, setCreatingDept] = useState(false);
  const [assigningDept, setAssigningDept] = useState(null);

  // Role change overlay
  const [roleTarget, setRoleTarget] = useState(null); // { user_id, name, current_role }

  const load = useCallback(() => {
    setLoading(true);
    getMembers(businessId)
      .then(d => { if (Array.isArray(d)) setMembers(d); setLoading(false); })
      .catch(() => setLoading(false)); // On error: keep existing members, just stop loading
  }, [businessId]);

  const loadDepts = useCallback(() => {
    setLoadingDepts(true);
    getDepartments(businessId).then(d => { setDepts(Array.isArray(d) ? d : []); setLoadingDepts(false); }).catch(() => setLoadingDepts(false));
  }, [businessId]);

  const loadInvites = useCallback(() => {
    getBizInvites(businessId).then(d => { if (Array.isArray(d)) setPendingInvites(d.filter(i => i.status === 'pending')); });
  }, [businessId]);

  useEffect(() => { load(); loadDepts(); loadInvites(); }, [load, loadDepts, loadInvites]);

  // ── User search (debounced) ────────────────────────────────────────────────
  const handleUserSearch = (q) => {
    setUserQuery(q);
    setSelectedUser(null);
    clearTimeout(searchTimer.current);
    if (q.trim().length < 2) { setUserResults([]); return; }
    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchUsers(q.trim(), currentUserId);
        const found = Array.isArray(res) ? res : [];
        setUserResults(found);
        // Auto-fill email field when no SL user found and query looks like an email
        if (found.length === 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim())) {
          setInviteEmail(q.trim());
        }
      } catch { setUserResults([]); }
      setSearchLoading(false);
    }, 350);
  };

  // ── Invite generation ──────────────────────────────────────────────────────
  const handleGenerateInvite = async () => {
    setGenerating(true);
    setInviteError('');
    try {
      const res = await createBizInvite(businessId, {
        email: inviteEmail.trim() || null,
        invited_user_id: selectedUser?.id || null,
        role: inviteRole,
        created_by: currentUserId,
      });
      if (res && res.token) {
        const url = `${APP_URL}/?bizinvite=${res.token}`;
        setGeneratedLink(url);
        setSelectedUser(null);
        setUserQuery('');
        setUserResults([]);
        loadInvites();
      } else {
        setInviteError(nl ? 'Kan geen link genereren. Probeer opnieuw.' : 'Could not generate link. Please try again.');
      }
    } catch {
      setInviteError(nl ? 'Fout bij genereren. Controleer je verbinding.' : 'Error generating invite. Check your connection.');
    }
    setGenerating(false);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(generatedLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const shareWhatsApp = () => {
    const name = bizName || (nl ? 'ons team' : 'our team');
    const msg = encodeURIComponent(
      nl
        ? `Hé! Je bent uitgenodigd om lid te worden van ${name} op SenseLifting.\n\nKlik op de link om je aan te sluiten:\n${generatedLink}`
        : `Hey! You've been invited to join ${name} on SenseLifting.\n\nClick the link to join:\n${generatedLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const shareEmail = () => {
    const name = bizName || (nl ? 'ons team' : 'our team');
    const subject = encodeURIComponent(nl ? `Uitnodiging voor ${name}` : `Invitation to join ${name}`);
    const body = encodeURIComponent(
      nl
        ? `Hallo,\n\nJe bent uitgenodigd om lid te worden van ${name} op SenseLifting — het platform voor teamwelzijn.\n\nKlik op de link om je aan te sluiten:\n${generatedLink}\n\nTot snel!`
        : `Hello,\n\nYou've been invited to join ${name} on SenseLifting — the team wellness platform.\n\nClick the link to join:\n${generatedLink}\n\nSee you there!`
    );
    window.open(`mailto:${inviteEmail || ''}?subject=${subject}&body=${body}`, '_blank');
  };

  // ── Notes ─────────────────────────────────────────────────────────────────
  const openNotes = async (uid) => {
    setNotesTarget(uid); setNoteLoading(true); setNoteText(''); setNoteSaved(false);
    const data = await getMemberNotes(businessId, uid);
    if (Array.isArray(data) && data.length > 0) setNoteText(data[0].note || '');
    setNoteLoading(false);
  };

  const handleSaveNote = async () => {
    if (!notesTarget) return;
    await saveMemberNotes(businessId, notesTarget, { manager_id: currentUserId, note: noteText });
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 2000);
  };

  // ── Role change ────────────────────────────────────────────────────────────
  const handleRoleChange = async (uid, newRole) => {
    await updateMemberRole(businessId, uid, newRole);
    setRoleTarget(null);
    load();
  };

  // ── Departments ────────────────────────────────────────────────────────────
  const handleAssignDept = async (uid, deptId) => {
    await setMemberDept(businessId, uid, { department_id: deptId });
    setAssigningDept(null);
    load();
  };
  const handleCreateDept = async () => {
    if (!newDeptName.trim()) return;
    setCreatingDept(true);
    await createDepartment(businessId, { name: newDeptName, color: newDeptColor });
    setNewDeptName('');
    setCreatingDept(false);
    loadDepts();
  };
  const handleDeleteDept = async (did) => {
    await deleteDepartment(businessId, did);
    loadDepts();
  };

  const handleRemove = async (uid) => {
    if (!window.confirm(nl ? 'Verwijder dit teamlid?' : 'Remove this team member?')) return;
    await removeMember(businessId, uid);
    load();
  };

  const revokeInvite = async (id) => {
    await deleteBizInvite(businessId, id);
    loadInvites();
  };

  if (loading) return <TechLoadingBar lang={lang} />;

  const activeCount = members.filter(m => m.status === 'active').length;
  const atMax       = activeCount >= MAX_SEATS;
  const nearMax     = activeCount >= MAX_SEATS - 5 && !atMax;
  const extraCost   = Math.max(0, activeCount - BASE_SEATS) * 12.50;
  const notesMember = notesTarget ? members.find(m => m.user_id === notesTarget) : null;

  return (
    <div style={{ padding: '0 0 24px' }}>
      <style>{CSS}</style>

      {/* Notes overlay */}
      {notesTarget && (
        <Portal>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,32,0.88)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: _T.bg2, borderRadius: '20px 20px 0 0', padding: 20, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>📝 {notesMember?.name || notesTarget}</div>
              <button onClick={() => setNotesTarget(null)} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Privé aantekening (alleen voor managers)' : 'Private note (managers only)'}</div>
            {noteLoading ? <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: 20 }}>...</div> : (
              <>
                <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={5}
                  placeholder={nl ? 'Aantekening over dit teamlid...' : 'Note about this team member...'}
                  style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), resize: 'none', fontSize: 13, fontFamily: 'inherit', marginBottom: 12 }} />
                <button onClick={handleSaveNote}
                  style={{ width: '100%', background: noteSaved ? `${C.teal}20` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: noteSaved ? `1px solid ${C.teal}` : 'none', borderRadius: 12, color: noteSaved ? C.teal : '#0a1220', fontSize: 14, fontWeight: 700, padding: '12px', cursor: 'pointer' }}>
                  {noteSaved ? (nl ? '✓ Opgeslagen' : '✓ Saved') : (nl ? 'Opslaan' : 'Save note')}
                </button>
              </>
            )}
          </div>
        </div>
        </Portal>
      )}

      {/* Dept assign overlay */}
      {assigningDept && (
        <Portal>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,32,0.88)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: _T.bg2, borderRadius: '20px 20px 0 0', padding: 20, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{nl ? 'Afdeling toewijzen' : 'Assign department'}</div>
              <button onClick={() => setAssigningDept(null)} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={() => handleAssignDept(assigningDept, null)}
                style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px 16px', cursor: 'pointer', textAlign: 'left' }}>
                {nl ? '— Geen afdeling' : '— No department'}
              </button>
              {depts.map(d => (
                <button key={d.id} onClick={() => handleAssignDept(assigningDept, d.id)}
                  style={{ background: _T.bg4, border: `2px solid ${d.color || _T.cardBorderBot}`, borderRadius: 12, color: C.white, fontSize: 13, padding: '11px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color || C.silver, display: 'inline-block' }} />
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Role change overlay */}
      {roleTarget && (isOwner || myRole === 'admin') && (
        <Portal>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,32,0.88)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: _T.bg2, borderRadius: '20px 20px 0 0', padding: 20, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{nl ? 'Rol wijzigen' : 'Change role'}</div>
              <button onClick={() => setRoleTarget(null)} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>{roleTarget.name}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => handleRoleChange(roleTarget.user_id, r.id)}
                  style={{ background: roleTarget.current_role === r.id ? `${r.color}20` : _T.bg4, border: `2px solid ${roleTarget.current_role === r.id ? r.color : _T.cardBorderBot}`, borderRadius: 12, color: C.white, fontSize: 13, padding: '12px 16px', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: r.color }}>{nl ? r.nl : r.label}</div>
                    <div style={{ fontSize: 11, color: C.silver, marginTop: 2 }}>{r.desc}</div>
                  </div>
                  {roleTarget.current_role === r.id && <span style={{ color: r.color, fontSize: 14 }}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Sub-tab strip */}
      <div style={{ display: 'flex', background: _T.bg4, borderRadius: 14, padding: 4, marginBottom: 16, gap: 4 }}>
        {[['members', nl ? '👥 Team' : '👥 Team'], ['invite', nl ? '✉ Uitnodigen' : '✉ Invite'], ['departments', nl ? '🗂 Afdelingen' : '🗂 Departments']].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            style={{ flex: 1, background: subTab === id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'transparent', border: 'none', borderRadius: 10, color: subTab === id ? '#0a1220' : C.silver, fontSize: 11, fontWeight: 700, padding: '9px 2px', cursor: 'pointer', transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── MEMBERS TAB ── */}
      {subTab === 'members' && (
        <>
          {/* Seat usage */}
          <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700 }}>{nl ? 'CAPACITEIT' : 'CAPACITY'}</div>
              <div style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>{activeCount} / {MAX_SEATS}</div>
            </div>
            <div style={{ height: 6, background: _T.bg4, borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${(activeCount / MAX_SEATS) * 100}%`, background: atMax ? C.red : nearMax ? C.orange : `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 3, transition: 'width 0.5s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: C.silver }}>{BASE_SEATS} {nl ? 'inbegrepen' : 'included'}</span>
              {activeCount > BASE_SEATS
                ? <span style={{ fontSize: 10, color: C.orange, fontWeight: 600 }}>+ €{extraCost.toFixed(2)} {nl ? 'extra/mnd' : 'extra/mo'}</span>
                : <span style={{ fontSize: 10, color: C.teal }}>✓ {nl ? 'Binnen seats' : 'Within seats'}</span>}
            </div>
          </div>

          {/* Member list */}
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>{nl ? 'TEAMLEDEN' : 'TEAM MEMBERS'} ({members.length})</div>
          {members.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: C.silver, fontSize: 13 }}>
              {nl ? 'Nog geen teamleden. Ga naar Uitnodigen om te beginnen.' : 'No team members yet. Go to Invite to get started.'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map((m, mi) => {
              const dept = depts.find(d => d.members?.some(dm => dm.user_id === m.user_id));
              const memberRole = m.role || (m.user_id === m.owner_id ? 'owner' : 'member');
              const initial = (m.name || '?').charAt(0).toUpperCase();
              const avgSleep = m.avg_sleep != null ? parseFloat(m.avg_sleep) : null;
              const avgMood  = m.avg_mood  != null ? parseFloat(m.avg_mood)  : null;
              const burnoutRisk = avgSleep !== null && avgSleep < 5 ? 'high'
                : avgMood !== null && avgMood < 2.5 ? 'high'
                : avgSleep !== null && avgSleep < 6.5 ? 'medium'
                : avgMood !== null && avgMood < 3.5 ? 'medium'
                : avgSleep === null && avgMood === null ? null
                : 'low';
              const burnoutPct = burnoutRisk === 'high' ? 80 : burnoutRisk === 'medium' ? 50 : burnoutRisk === 'low' ? 20 : null;
              return (
                <div key={m.user_id || mi} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px 14px', cursor: onMemberClick ? 'pointer' : 'default' }}
                  onClick={() => onMemberClick && onMemberClick(m)}>

                  {/* Top row: avatar + name/badges + burnout circle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${_T.accent}18`, border: `2px solid ${_T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                      {m.picture ? <img src={m.picture} alt="" style={{ width: 38, height: 38, objectFit: 'cover' }} /> : <span style={{ fontSize: 16, fontWeight: 700, color: _T.accent }}>{initial}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ color: C.white, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                        <RoleBadge role={memberRole} nl={nl} />
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {dept && <span style={{ fontSize: 9, color: dept.color || C.silver, background: `${dept.color || C.silver}18`, border: `1px solid ${dept.color || C.silver}33`, borderRadius: 6, padding: '1px 6px', fontWeight: 700 }}>{dept.name}</span>}
                        {m.status === 'pending' && <span style={{ fontSize: 10, color: C.silver }}>⏳ {nl ? 'In afwachting' : 'Pending'}</span>}
                      </div>
                    </div>
                    {/* Burnout risk indicator */}
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      {burnoutPct !== null
                        ? <BurnoutCircle risk={burnoutPct} />
                        : <div style={{ width: 46, height: 46, borderRadius: '50%', background: _T.bg4, border: `2px solid ${_T.cardBorderBot}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 10, color: C.silver }}>—</span>
                          </div>
                      }
                      <div style={{ fontSize: 8, color: '#6b7a99', marginTop: 1 }}>{nl ? 'Burn-out' : 'Burnout'}</div>
                    </div>
                  </div>

                  {/* Metrics bars — real sleep/mood data */}
                  <div style={{ paddingLeft: 50 }}>
                    <ScoreBar label={nl ? 'Slaap (gem. 14d)' : 'Sleep (avg 14d)'} score={avgSleep !== null ? Math.round((avgSleep / 10) * 100) : 0} color="#5B9ED0" />
                    <ScoreBar label={nl ? 'Stemming (gem. 14d)' : 'Mood (avg 14d)'} score={avgMood !== null ? Math.round((avgMood / 5) * 100) : 0} color={_T.accent} />
                    {avgSleep === null && avgMood === null && (
                      <div style={{ fontSize: 10, color: C.silver, fontStyle: 'italic' }}>{nl ? 'Nog geen check-in data' : 'No check-in data yet'}</div>
                    )}
                  </div>

                  {/* Manager actions */}
                  {canManage && m.user_id !== currentUserId && (
                    <div style={{ display: 'flex', gap: 5, marginTop: 10, paddingLeft: 50 }} onClick={e => e.stopPropagation()}>
                      {(isOwner || myRole === 'admin') && memberRole !== 'owner' && (
                        <button onClick={() => setRoleTarget({ user_id: m.user_id, name: m.name, current_role: memberRole })}
                          title={nl ? 'Rol wijzigen' : 'Change role'}
                          style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '5px 8px' }}>👑</button>
                      )}
                      <button onClick={() => setAssigningDept(m.user_id)}
                        title={nl ? 'Afdeling' : 'Department'}
                        style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '5px 8px' }}>🗂</button>
                      <button onClick={() => openNotes(m.user_id)}
                        title={nl ? 'Notities' : 'Notes'}
                        style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '5px 8px' }}>📝</button>
                      {(isOwner || myRole === 'admin') && memberRole !== 'owner' && (
                        <button onClick={() => handleRemove(m.user_id)}
                          style={{ background: 'none', border: 'none', color: `${C.red}88`, cursor: 'pointer', fontSize: 17, padding: '2px 4px' }}>✕</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── INVITE TAB ── */}
      {subTab === 'invite' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>{nl ? 'TEAMLID UITNODIGEN' : 'INVITE TEAM MEMBER'}</div>

          {!canManage && (
            <div style={{ textAlign: 'center', color: C.silver, padding: 32, fontSize: 13 }}>
              {nl ? 'Alleen managers of admins kunnen uitnodigingen sturen.' : 'Only managers or admins can send invitations.'}
            </div>
          )}

          {canManage && (
            <>
              {/* Invite generator */}
              <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 16 }}>
                {/* User search — find existing SL users */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Zoek SenseLifting gebruiker' : 'Search SenseLifting user'}</div>
                  {selectedUser ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 10, padding: '8px 12px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                        {selectedUser.picture
                          ? <img src={selectedUser.picture} alt="" style={{ width: 32, height: 32, objectFit: 'cover' }} />
                          : <span style={{ fontSize: 14, fontWeight: 700, color: _T.accent }}>{(selectedUser.name||'?').charAt(0).toUpperCase()}</span>}
                      </div>
                      <span style={{ flex: 1, color: C.white, fontSize: 13, fontWeight: 600 }}>{selectedUser.name}</span>
                      <button onClick={() => { setSelectedUser(null); setUserQuery(''); }}
                        style={{ background: 'none', border: 'none', color: C.silver, fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input value={userQuery} onChange={e => handleUserSearch(e.target.value)}
                        placeholder={nl ? 'Naam of e-mail...' : 'Name or email...'}
                        style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13 }} />
                      {searchLoading && <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: C.silver, fontSize: 12 }}>...</div>}
                      {userResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: _T.bg2, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, overflow: 'hidden', marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                          {userResults.map(u => (
                            <button key={u.id} onClick={() => { setSelectedUser(u); setUserQuery(u.name); setUserResults([]); setInviteEmail(''); }}
                              style={{ width: '100%', background: 'none', border: 'none', borderBottom: `1px solid ${_T.cardBorderBot}44`, padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                {u.picture
                                  ? <img src={u.picture} alt="" style={{ width: 28, height: 28, objectFit: 'cover' }} />
                                  : <span style={{ fontSize: 12, fontWeight: 700, color: _T.accent }}>{(u.name||'?').charAt(0).toUpperCase()}</span>}
                              </div>
                              <span style={{ color: C.white, fontSize: 13 }}>{u.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ color: C.silver, fontSize: 10, marginTop: 4 }}>
                    {nl ? 'Geselecteerde gebruiker krijgt direct een melding in de app.' : 'Selected user gets an instant in-app notification.'}
                  </div>
                </div>

              {/* Email (only shown when no user selected) */}
                {!selectedUser && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>
                    {nl ? 'Nog geen account op SenseLifting? Stuur een e-mailuitnodiging:' : 'Not on SenseLifting yet? Send an email invite:'}
                  </div>
                  <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder={nl ? 'naam@bedrijf.nl' : 'name@company.com'}
                    type="email"
                    style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13 }} />
                  {inviteEmail && (
                    <div style={{ color: '#4AC87A', fontSize: 10, marginTop: 4 }}>
                      ✓ {nl ? 'Er wordt een uitnodigingsmail verstuurd naar dit adres.' : 'An invite email will be sent to this address.'}
                    </div>
                  )}
                </div>
                )}

                {/* Role selector */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Rol in het team' : 'Team role'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {ROLES.map(r => (
                      <button key={r.id} onClick={() => setInviteRole(r.id)}
                        style={{ background: inviteRole === r.id ? `${r.color}18` : _T.bg4, border: `2px solid ${inviteRole === r.id ? r.color : _T.cardBorderBot}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                        <div>
                          <span style={{ color: inviteRole === r.id ? r.color : C.white, fontSize: 13, fontWeight: 700 }}>{nl ? r.nl : r.label}</span>
                          <span style={{ color: C.silver, fontSize: 11, marginLeft: 8 }}>{r.desc}</span>
                        </div>
                        {inviteRole === r.id && <span style={{ color: r.color, fontSize: 14 }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Error */}
                {inviteError && <div style={{ color: C.red, fontSize: 11, marginBottom: 10, padding: '8px 12px', background: `${C.red}15`, border: `1px solid ${C.red}44`, borderRadius: 8 }}>{inviteError}</div>}

                {/* Generate button */}
                <button onClick={handleGenerateInvite} disabled={generating || atMax}
                  style={{ width: '100%', background: generating || atMax ? `${_T.accent}44` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '13px', cursor: generating || atMax ? 'default' : 'pointer' }}>
                  {generating ? '...' : (nl ? '✉ Uitnodiging versturen' : '✉ Send invite')}
                </button>
              </div>

              {/* Generated link + share buttons */}
              {generatedLink && (
                <div style={{ background: `${_T.accent}10`, border: `1px solid ${_T.accent}44`, borderRadius: 16, padding: '16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: _T.accent, fontWeight: 700, marginBottom: 10 }}>
                    {nl ? '✓ Uitnodiging verzonden!' : '✓ Invite sent!'}
                  </div>

                  {/* Link display */}
                  <div style={{ background: _T.bg4, borderRadius: 10, padding: '10px 12px', fontSize: 11, color: C.silver, wordBreak: 'break-all', marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
                    {generatedLink}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {/* Copy */}
                    <button onClick={copyLink}
                      style={{ background: linkCopied ? `${C.teal}20` : _T.bg4, border: `1px solid ${linkCopied ? C.teal : _T.cardBorderBot}`, borderRadius: 10, color: linkCopied ? C.teal : C.white, fontSize: 12, fontWeight: 600, padding: '10px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 18 }}>{linkCopied ? '✓' : '📋'}</span>
                      <span>{linkCopied ? (nl ? 'Gekopieerd' : 'Copied') : (nl ? 'Kopieer' : 'Copy')}</span>
                    </button>

                    {/* WhatsApp */}
                    <button onClick={shareWhatsApp}
                      style={{ background: '#25D36615', border: '1px solid #25D36644', borderRadius: 10, color: '#25D366', fontSize: 12, fontWeight: 700, padding: '10px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 18 }}>💬</span>
                      <span>WhatsApp</span>
                    </button>

                    {/* Email */}
                    <button onClick={shareEmail}
                      style={{ background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 10, color: _T.accent, fontSize: 12, fontWeight: 700, padding: '10px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 18 }}>✉️</span>
                      <span>Email</span>
                    </button>
                  </div>

                  <button onClick={() => { setGeneratedLink(null); setInviteEmail(''); }}
                    style={{ width: '100%', background: 'none', border: 'none', color: C.silver, fontSize: 12, marginTop: 12, cursor: 'pointer', padding: '6px 0' }}>
                    {nl ? '← Nieuwe uitnodiging' : '← New invite'}
                  </button>
                </div>
              )}

              {/* Pending invites */}
              {pendingInvites.length > 0 && (
                <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px' }}>
                  <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
                    {nl ? 'OPENSTAANDE UITNODIGINGEN' : 'PENDING INVITES'} ({pendingInvites.length})
                  </div>
                  {pendingInvites.map(inv => {
                    const r = ROLES.find(x => x.id === inv.role);
                    return (
                      <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid ' + _T.cardBorderBot + '44' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {inv.invited_user_picture && (
                            <img src={inv.invited_user_picture} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          )}
                          <div>
                            <div style={{ fontSize: 12, color: C.white }}>
                              {inv.invited_user_name || inv.email || (nl ? 'Directe uitnodiging' : 'Direct invite')}
                            </div>
                            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>
                              <span style={{ color: r?.color || C.silver }}>{nl ? r?.nl || inv.role : r?.label || inv.role}</span>
                              {' · '}{new Date(inv.created_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB')}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => revokeInvite(inv.id)}
                          style={{ background: 'none', border: 'none', color: `${C.red}88`, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── DEPARTMENTS TAB ── */}
      {subTab === 'departments' && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'AFDELINGEN / TEAMS' : 'DEPARTMENTS / TEAMS'}</div>
          {canManage && (
            <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 10 }}>{nl ? 'Nieuwe afdeling' : 'New department'}</div>
              <input value={newDeptName} onChange={e => setNewDeptName(e.target.value)} placeholder={nl ? 'Afdelingsnaam' : 'Department name'}
                style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13, marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {DEPT_COLORS.map(col => (
                  <button key={col} onClick={() => setNewDeptColor(col)}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: col, border: `3px solid ${newDeptColor === col ? C.white : 'transparent'}`, cursor: 'pointer', flexShrink: 0 }} />
                ))}
              </div>
              <button onClick={handleCreateDept} disabled={creatingDept || !newDeptName.trim()}
                style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '11px', cursor: creatingDept ? 'default' : 'pointer', width: '100%' }}>
                {creatingDept ? '...' : (nl ? '+ Aanmaken' : '+ Create')}
              </button>
            </div>
          )}
          {loadingDepts ? <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
            : depts.length === 0 ? <div style={{ textAlign: 'center', padding: 32, color: C.silver, fontSize: 13 }}>{nl ? 'Nog geen afdelingen.' : 'No departments yet.'}</div>
            : depts.map(d => (
              <div key={d.id} style={{ background: _T.bg3, border: `2px solid ${d.color || _T.cardBorderBot}33`, borderRadius: 16, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: d.color || C.silver, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{d.name}</span>
                    <span style={{ color: C.silver, fontSize: 11 }}>({d.members?.length || 0})</span>
                  </div>
                  {canManage && (
                    <button onClick={() => handleDeleteDept(d.id)} style={{ background: 'none', border: 'none', color: `${C.red}66`, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
                  )}
                </div>
                {(d.members || []).length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {d.members.map(m => (
                      <span key={m.user_id} style={{ background: `${d.color || C.silver}15`, border: `1px solid ${d.color || C.silver}33`, borderRadius: 8, padding: '4px 10px', fontSize: 11, color: C.silver }}>{m.name}</span>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: C.silver, fontSize: 12 }}>{nl ? 'Geen leden — wijs toe via het team overzicht.' : 'No members — assign via team overview.'}</div>
                )}
              </div>
            ))}
        </>
      )}
    </div>
  );
}
